import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiCheck, FiMinus } from "react-icons/fi";
import plans from "../../data/plans";
import membershipFaqs from "../../data/faqs";
import PageBanner from "../../components/layout/PageBanner";
import MembershipCard from "../../components/common/MembershipCard";
import SectionHeading from "../../components/common/SectionHeading";
import FAQAccordion from "../../components/common/FAQAccordion";
import { validateEnquiryForm } from "../../utils/validators";
import "./Membership.css";

const COMPARE_ROWS = [
  { label: "Gym floor access", monthly: true, quarterly: true, annual: true },
  { label: "Locker facility", monthly: true, quarterly: true, annual: true },
  { label: "Group classes", monthly: true, quarterly: true, annual: true },
  { label: "Personal training sessions", monthly: false, quarterly: true, annual: true },
  { label: "Diet guidance", monthly: false, quarterly: true, annual: true },
  { label: "Priority booking", monthly: false, quarterly: true, annual: true },
  { label: "Progress tracking", monthly: false, quarterly: false, annual: true },
  { label: "Guest passes", monthly: false, quarterly: false, annual: true },
];

const initialForm = { fullName: "", phone: "", email: "", goal: "", program: "", message: "" };

export default function Membership() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateEnquiryForm({ ...form, consent: true });
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      setSubmitted(false);
      return;
    }
    setErrors({});
    setSubmitted(true);
    // TODO: connect to enquiry API endpoint once available.
  };

  return (
    <>
      <Helmet>
        <title>Membership Plans | Firm Fit Factory Peelamedu, Coimbatore</title>
        <meta name="description" content="Compare Firm Fit Factory membership plans — Monthly, Quarterly and Annual. Contact us for current pricing and trial sessions." />
        <link rel="canonical" href="https://www.firmfitfactory.com/membership" />
      </Helmet>

      <PageBanner eyebrow="Membership" title="Choose Your Plan" description="Straightforward pricing, no confusing tiers. All prices are managed in one place — contact us for current rates." />

      <section className="section">
        <div className="container">
          <div className="membership-page__grid">
            {plans.map((plan) => <MembershipCard plan={plan} key={plan.id} />)}
          </div>
        </div>
      </section>

      <section className="section membership-compare">
        <div className="container">
          <SectionHeading eyebrow="Compare Plans" title="What's" highlight="included" />
          <div className="membership-compare__table-wrap">
            <table className="membership-compare__table">
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">Monthly</th>
                  <th scope="col">Quarterly</th>
                  <th scope="col">Annual</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td>{row.monthly ? <FiCheck aria-label="Included" /> : <FiMinus aria-label="Not included" />}</td>
                    <td>{row.quarterly ? <FiCheck aria-label="Included" /> : <FiMinus aria-label="Not included" />}</td>
                    <td>{row.annual ? <FiCheck aria-label="Included" /> : <FiMinus aria-label="Not included" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="section membership-faq">
        <div className="container membership-faq__grid">
          <div>
            <SectionHeading eyebrow="Questions" title="Membership" highlight="FAQ" />
            <FAQAccordion items={membershipFaqs} />
          </div>

          <div className="membership-enquiry">
            <h3>Request a Trial Session</h3>
            <p>Fill in your details and our team will get back to you.</p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="membership-enquiry__field">
                <label htmlFor="m-name">Full Name</label>
                <input id="m-name" type="text" value={form.fullName} onChange={handleChange("fullName")} />
                {errors.fullName && <span className="membership-enquiry__error">{errors.fullName}</span>}
              </div>
              <div className="membership-enquiry__field">
                <label htmlFor="m-phone">Phone Number</label>
                <input id="m-phone" type="tel" value={form.phone} onChange={handleChange("phone")} />
                {errors.phone && <span className="membership-enquiry__error">{errors.phone}</span>}
              </div>
              <div className="membership-enquiry__field">
                <label htmlFor="m-email">Email Address</label>
                <input id="m-email" type="email" value={form.email} onChange={handleChange("email")} />
                {errors.email && <span className="membership-enquiry__error">{errors.email}</span>}
              </div>
              <div className="membership-enquiry__field">
                <label htmlFor="m-goal">Fitness Goal</label>
                <select id="m-goal" value={form.goal} onChange={handleChange("goal")}>
                  <option value="">Select a goal</option>
                  <option>Build Strength</option>
                  <option>Lose Weight</option>
                  <option>Build Muscle</option>
                  <option>General Fitness</option>
                </select>
                {errors.goal && <span className="membership-enquiry__error">{errors.goal}</span>}
              </div>
              <div className="membership-enquiry__field">
                <label htmlFor="m-program">Preferred Program</label>
                <select id="m-program" value={form.program} onChange={handleChange("program")}>
                  <option value="">Select a program</option>
                  <option>Strength Training</option>
                  <option>Functional Training</option>
                  <option>Weight Management</option>
                  <option>Personal Training</option>
                  <option>Cardio Conditioning</option>
                  <option>Muscle Building</option>
                </select>
                {errors.program && <span className="membership-enquiry__error">{errors.program}</span>}
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>
                Request Trial
              </button>
              {submitted && <p className="membership-enquiry__success" role="status">Thanks! Your enquiry has been noted — we'll be in touch shortly.</p>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
